import { Check, X } from 'lucide-react';

/**
 * Karta notifikace — jeden vzhled pro všechna tři místa, kde notifikace žije:
 * seznam na stránce Aktivita, zvoneček na počítači a toast, který přiletí.
 *
 * Dřív si každé místo kreslilo vlastní. Seznam měl barevný proužek u levé hrany
 * **a** k tomu štítek modulu v téže barvě — dvakrát totéž. Zvoneček měl místo
 * toho kolečko s ikonou modulu, tedy jiný jazyk. A toast měl pro všechno
 * stejnou oranžovou ikonu zvonku, takže ze zprávy od kolegy a z upozornění
 * Směn byla na první pohled táž věc.
 *
 * Skladba je všude stejná:
 *
 *     ┌──────────────────────────────────────────────┐
 *     │ [ikona] Modul · před 25 min               •  │  ← • = nepřečteno
 *     │         Titulek                              │
 *     │         Zpráva na dva řádky                  │
 *     │         [Akce →]                         [✓] │
 *     └──────────────────────────────────────────────┘
 *
 * **Identitu modulu nese jedna věc — dlaždice s ikonou v jeho barvě.** Je to
 * tentýž tvar, jakým moduly mluví jinde (dlaždice v Přehledu, hlavičky
 * modulů), takže notifikace ze Směn vypadá jako Směny, ne jako obecné hlášení.
 *
 * **Nepřečteno říká tečka, ne průhlednost.** Vyřízené notifikace se dřív
 * ztlumily na 72 % krytí, což v tmavém motivu ubralo text pod hranici
 * čitelnosti. Teď se mění barva titulku a dlaždice zšedne; text zůstává celý.
 *
 * @param {Function} props.Ikona - ikona modulu (lucide)
 * @param {string} props.barva - barva modulu
 * @param {string} [props.avatar] - fotka odesílatele (chat); přebije ikonu
 * @param {string} [props.stitek] - název modulu do meta řádku
 * @param {string} [props.cas] - relativní čas („před 25 min")
 * @param {string} [props.typBarva] - barva typu (info/varování/chyba) jako
 *        puntík u dlaždice; bez ní se puntík nekreslí
 * @param {string} props.titulek
 * @param {string} [props.zprava]
 * @param {boolean} [props.neprectena] - kreslí tečku vpravo v meta řádku
 * @param {boolean} [props.ztlumena] - vyřízená/přečtená
 * @param {Array<{id, label, Icon, onClick, ton}>} [props.akce] - tlačítka dole;
 *        `ton`: 'modul' (výchozí) | 'ok' | 'ne'
 * @param {Function} [props.naPrecteno] - ✓ vpravo dole („označit jako přečtené")
 * @param {Function} [props.naKliknuti] - klepnutí na celou kartu
 * @param {Function} [props.zavrit] - × vpravo nahoře (toast)
 * @param {React.ReactNode} [props.pod] - pruh pod obsahem (postup úkolu, odpočet toastu)
 * @param {'karta'|'radek'|'sklo'} [props.varianta]
 * @param {boolean} [props.isDark]
 */
export default function NotifikaceKarta({
    Ikona,
    barva = '#FF8A00',
    avatar = null,
    stitek = null,
    cas = null,
    typBarva = null,
    titulek,
    zprava = null,
    neprectena = false,
    ztlumena = false,
    akce = [],
    naPrecteno = null,
    naKliknuti = null,
    zavrit = null,
    pod = null,
    varianta = 'karta',
    isDark = false,
}) {
    const plocha = {
        karta: {
            background: isDark ? 'rgba(255,255,255,0.055)' : 'var(--surface-raised)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
            boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.06)' : 'var(--shadow-xs)',
            borderRadius: 12,
            padding: 12,
        },
        radek: { background: 'transparent', borderRadius: 10, padding: '10px 12px' },
        // Toast leží nad libovolným obsahem, i nad barevnou plochou cockpitu.
        // Modálové sklo (--modal-bg) je na to moc průhledné — text se pral
        // s tím, co prosvítalo. Bere proto --surface-overlay, plochu určenou
        // přesně na tohle, a rozostření drží dojem skla.
        sklo: {
            background: 'var(--surface-overlay)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
            boxShadow: 'var(--modal-shadow)',
            backdropFilter: 'blur(20px) saturate(150%)',
            WebkitBackdropFilter: 'blur(20px) saturate(150%)',
            borderRadius: 14,
            padding: 12,
        },
    }[varianta];

    const barvaDlazdice = ztlumena ? (isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)') : barva;
    const pozadiDlazdice = ztlumena
        ? (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(20,20,40,0.06)')
        : `${barva}1f`;

    const tonAkce = (ton) => {
        if (ton === 'ok') return { bg: 'rgba(16,185,129,0.15)', barva: isDark ? '#34D399' : '#07835A' };
        if (ton === 'ne') return { bg: 'rgba(216,90,48,0.15)', barva: isDark ? '#F58C6A' : '#B03A12' };
        return { bg: `${barva}1f`, barva };
    };

    return (
        <div
            onClick={naKliknuti || undefined}
            style={{
                ...plocha,
                display: 'flex',
                gap: 12,
                position: 'relative',
                cursor: naKliknuti ? 'pointer' : 'default',
                transition: 'background 0.15s ease, border-color 0.15s ease',
            }}
        >
            {/* Dlaždice modulu — jediný nositel identity */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
                {avatar ? (
                    <img src={avatar} alt="" style={{ width: 38, height: 38, borderRadius: 11, objectFit: 'cover', display: 'block' }} />
                ) : (
                    <div style={{
                        width: 38, height: 38, borderRadius: 11,
                        background: pozadiDlazdice,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        {Ikona && <Ikona size={18} style={{ color: barvaDlazdice }} />}
                    </div>
                )}
                {/* Typ (info · hotovo · pozor · chyba) visí na dlaždici, ať
                    nestojí bez vysvětlení uprostřed meta řádku. */}
                {typBarva && !ztlumena && (
                    <span aria-hidden="true" style={{
                        position: 'absolute', right: -2, bottom: -2,
                        width: 10, height: 10, borderRadius: '50%',
                        background: typBarva,
                        border: `2px solid ${isDark ? '#15151b' : '#fff'}`,
                    }} />
                )}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                {/* Meta — modul, čas a tečka nepřečteného */}
                {(stitek || cas || neprectena) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                        {stitek && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: ztlumena ? 'var(--text-muted)' : barva, whiteSpace: 'nowrap' }}>
                                {stitek}
                            </span>
                        )}
                        {stitek && cas && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>·</span>}
                        {cas && <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{cas}</span>}
                        {neprectena && (
                            <span aria-label="nepřečteno" style={{
                                marginLeft: 'auto', width: 7, height: 7, borderRadius: '50%',
                                background: barva, flexShrink: 0,
                            }} />
                        )}
                    </div>
                )}

                <h3 style={{
                    fontSize: 14, fontWeight: 600, lineHeight: 1.3, margin: 0,
                    color: ztlumena ? 'var(--text-secondary)' : 'var(--text-primary)',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                    {titulek}
                </h3>

                {zprava && (
                    <p style={{
                        fontSize: 12.5, lineHeight: 1.45, margin: '3px 0 0',
                        color: ztlumena ? 'var(--text-muted)' : 'var(--text-secondary)',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>
                        {zprava}
                    </p>
                )}

                {pod}

                {(akce.length > 0 || naPrecteno) && (
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                        {akce.map((a) => {
                            const t = tonAkce(a.ton);
                            const AIkona = a.Icon;
                            return (
                                <button key={a.id || a.label} type="button"
                                    onClick={(e) => { e.stopPropagation(); a.onClick?.(); }}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        height: 32, padding: '0 12px', borderRadius: 8,
                                        background: t.bg, color: t.barva, border: 'none',
                                        // Bez `font: inherit` — ta zkratka stála za velikostí
                                        // a tloušťkou a vracela je na zděděné (tlačítka byla
                                        // písmem větší než titulek karty).
                                        fontSize: 12.5, fontWeight: 600, lineHeight: 1,
                                        fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap',
                                    }}>
                                    {AIkona && <AIkona size={14} />}
                                    {a.label}
                                </button>
                            );
                        })}
                        {naPrecteno && (
                            <button type="button" aria-label="Označit jako přečtené" title="Označit jako přečtené"
                                onClick={(e) => { e.stopPropagation(); naPrecteno(); }}
                                style={{
                                    marginLeft: 'auto', width: 32, height: 32, borderRadius: 8,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(20,20,40,0.05)',
                                    color: 'var(--text-secondary)', border: 'none', cursor: 'pointer', flexShrink: 0,
                                }}>
                                <Check size={15} />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {zavrit && (
                <button type="button" aria-label="Zavřít"
                    onClick={(e) => { e.stopPropagation(); zavrit(); }}
                    style={{
                        position: 'absolute', top: 6, right: 6,
                        width: 28, height: 28, borderRadius: 8,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer',
                    }}>
                    <X size={15} />
                </button>
            )}
        </div>
    );
}
